import React from 'react';
import { PAYPAL_CLIENT_ID } from '@reverbdotcom/env';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import {
  CoreCheckoutStartPaypalCheckout,
  core_apimessages_Error as Error,
  CoreCheckoutUpdatePaypalShippingEstimate,
  core_apimessages_CheckoutLineItem_Type as CheckoutLineItemType,
} from '../schemas/rql';
import { MutationFn, ChildProps } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import Location from '../lib/wrapped_location';
import {
  OnShippingChangeData,
  OnShippingChangeActions,
} from '@paypal/paypal-js';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useEventTracker } from './trackPayPalEvent';
import { Paths } from './shared/url_helpers';

type StartPaypalCheckoutMutationFn = MutationFn<
  CoreCheckoutStartPaypalCheckout.Mutation,
  CoreCheckoutStartPaypalCheckout.Variables
>;

type UpdatePaypalShippingEstimateMutationFn = MutationFn<
  CoreCheckoutUpdatePaypalShippingEstimate.Mutation,
  CoreCheckoutUpdatePaypalShippingEstimate.Variables
>;

interface MutationProps {
  startPaypalCheckout: StartPaypalCheckoutMutationFn;
  updatePaypalShippingEstimate: UpdatePaypalShippingEstimateMutationFn;
}

interface ExternalProps {
  currency: string;
  quantity?: number;
  checkoutBundlingId: string;
  onError: (errors: Error[]) => void;
  variant: 'buy-now' | 'cart';
  parentComponentName: string;
  listingCurrency: string;
}

type Props = ChildProps<ExternalProps & MutationProps>;

export function CheckoutPayPalButton(props: Props) {
  const { variant, listingCurrency, parentComponentName, currency, quantity, checkoutBundlingId, onError } = props;
  const [startPaypalCheckout, { errors: startMutationErrors }] = useMutation(props.startPaypalCheckout);
  const [updateCheckoutShippingEstimate, { errors: updateShippingMutationErrors }] = useMutation(props.updatePaypalShippingEstimate);
  const checkoutRef = React.useRef('');

  React.useEffect(() => {
    onError([...startMutationErrors, ...updateShippingMutationErrors]);
  }, [onError, startMutationErrors, updateShippingMutationErrors]);

  const trackPaypalEvent = useEventTracker({ variant, listingCurrency, parentComponentName, currency, quantity });

  async function createOrder() {
    trackPaypalEvent(MParticleEventName.ClickedExpressPaypalButton);

    const result = await startPaypalCheckout({
      variables: {
        input: {
          checkoutBundlingId,
          quantity,
        },
      },
    });

    if (result?.data?.startPaypalCheckout.paypalOrderId) {
      checkoutRef.current = result.data.startPaypalCheckout.checkout?.id;
    }

    return result?.data?.startPaypalCheckout.paypalOrderId;
  }

  async function onApprove() {
    trackPaypalEvent(MParticleEventName.ApprovedExpressPaypalPayment);

    const reviewUrl = Paths.paypalCheckoutReview.expand({
      checkoutBundlingId,
      checkout_uuid: checkoutRef.current,
    });

    await Location.assign(reviewUrl);
  }

  async function onShippingChange(data: OnShippingChangeData, actions: OnShippingChangeActions) {
    const { city, postal_code, state, country_code } = data.shipping_address;

    const result = await updateCheckoutShippingEstimate({
      variables: {
        input: {
          id: checkoutRef.current,
          shippingAddress: {
            locality: city,
            postalCode: postal_code,
            region: state,
            countryCode: country_code,
          },
        },
      },
    });

    return actions.order.patch([
      {
        op: 'replace',
        path: "/purchase_units/@reference_id=='default'/amount",
        value: buildUpdatePaypalOrderInput(result.data.updateCheckoutShippingEstimate.checkout),
      },
    ]);
  }

  function onPaypalError() {
    const error = { message: I18n.t('discovery.checkout.paypal.error') };
    onError([error]);
  }

  const options = {
    currency,
    clientId: PAYPAL_CLIENT_ID,
    intent: 'capture',
    dataNamespace: `${props.parentComponentName}-${props.checkoutBundlingId}`,
    components: 'buttons',
    commit: false,
  };

  return (
    <PayPalScriptProvider options={options}>
      <PayPalButtons
        fundingSource="paypal"
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onPaypalError}
        onShippingChange={onShippingChange}
        style={{
          shape: 'pill',
          height: 36,
        }}
      />
    </PayPalScriptProvider>
  );
}

function buildUpdatePaypalOrderInput(checkout) {
  function buildPaypalLineItem(lineItemType) {
    return {
      value: checkout.lineItems.find(lineItem => lineItem.type === lineItemType).amount.amount,
      currency_code: checkout.lineItems.find(lineItem => lineItem.type === lineItemType).amount.currency,
    };
  }

  return {
    ...buildPaypalLineItem(CheckoutLineItemType.AMOUNT_OWED),
    breakdown: {
      item_total: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_LISTING_SUBTOTAL),
      tax_total: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_TAX),
      shipping: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_SHIPPING),
    },
  };
}

export function isCartEligible(attributes: {
  currencyCount: number,
  bundleCount: number,
  shopCount: number,
  isLocalPickupOnly: boolean,
  includesStandalonePaypal: boolean,
}) {
  const { currencyCount, bundleCount, shopCount, isLocalPickupOnly, includesStandalonePaypal } = attributes;

  return currencyCount === 1 && bundleCount === 1 && shopCount === 1 && !isLocalPickupOnly && !includesStandalonePaypal;
}

const withStartPaypalCheckoutMutation = withGraphql<
  ExternalProps,
  CoreCheckoutStartPaypalCheckout.Mutation,
  CoreCheckoutStartPaypalCheckout.Variables
>(
  gql`
  mutation Core_Checkout_StartPaypalCheckout(
    $input: Input_core_apimessages_StartPaypalCheckoutRequest
  ) {
    startPaypalCheckout(input: $input) {
      checkout {
        _id
        id
      }
      paypalOrderId
    }
  }`,
  {
    name: 'startPaypalCheckout',
  },
);

const withUpdateShippingEstimateMutation = withGraphql<
  ExternalProps,
  CoreCheckoutUpdatePaypalShippingEstimate.Mutation,
  CoreCheckoutUpdatePaypalShippingEstimate.Variables
>(
  gql`
  mutation Core_Checkout_UpdatePaypalShippingEstimate(
    $input: Input_core_apimessages_CheckoutUpdateShippingEstimateRequest
  ) {
    updateCheckoutShippingEstimate(input: $input) {
      checkout {
        _id
        id
        lineItems {
          type
          amount {
            amount
            currency
          }
        }
      }
    }
  }`,
  {
    name: 'updatePaypalShippingEstimate',
  },
);

export default connect<ExternalProps>([
  withStartPaypalCheckoutMutation,
  withUpdateShippingEstimateMutation,
])(CheckoutPayPalButton);
